import { databases, DATABASE_ID, COLLECTIONS, ID, Query } from "./appwrite";
import type { Models } from "appwrite";

export interface Subscription {
  $id: string;
  userId: string;
  subjectId: string;
  plan: "free" | "basic" | "pro" | "monthly" | "quarterly" | "yearly" | "lifetime";
  status: "active" | "expired" | "cancelled";
  startDate: string;
  endDate: string | null;
  createdAt: string;
}

/**
 * Get all subscriptions for a user
 */
export async function getUserSubscriptions(
  userId: string
): Promise<Subscription[]> {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTIONS.SUBSCRIPTIONS,
      [Query.equal("userId", userId)]
    );
    return response.documents as unknown as Subscription[];
  } catch (error: any) {
    console.error("Error fetching subscriptions:", error);
    // If collection doesn't exist or no permissions, return empty array
    // This allows the app to work even before collections are set up
    if (error.code === 404) {
      console.warn(
        "⚠️ Subscriptions collection not found. Please create it in Appwrite Console."
      );
    }
    return [];
  }
}

/**
 * Check if user has active subscription for a subject
 */
export async function hasActiveSubscription(
  userId: string,
  subjectId: string
): Promise<boolean> {
  const response = await databases.listDocuments(
    DATABASE_ID,
    COLLECTIONS.SUBSCRIPTIONS,
    [
      Query.equal("userId", userId),
      Query.equal("subjectId", subjectId),
      Query.equal("status", "active"),
    ]
  );

  if (response.total === 0) return false;

  const sub = response.documents[0] as unknown as Subscription;

  // Check if not expired
  if (sub.endDate) {
    return new Date(sub.endDate) > new Date();
  }

  // Free plan has no end date
  return true;
}

/**
 * Check if user can access content (demo or subscribed)
 */
export async function canAccessContent(
  userId: string,
  subjectId: string,
  isDemo: boolean = false
): Promise<boolean> {
  // Demo content is always accessible for authenticated users
  if (isDemo) return true;

  return await hasActiveSubscription(userId, subjectId);
}

/**
 * Create a free subscription for a user (on signup)
 */
export async function createFreeSubscription(
  userId: string,
  subjectId: string
): Promise<Subscription> {
  const doc = await databases.createDocument(
    DATABASE_ID,
    COLLECTIONS.SUBSCRIPTIONS,
    ID.unique(),
    {
      userId,
      subjectId,
      plan: "free",
      status: "active",
      startDate: new Date().toISOString(),
      endDate: null,
      createdAt: new Date().toISOString(),
    }
  );
  return doc as unknown as Subscription;
}

/**
 * Get subjects the user has access to
 */
export async function getAccessibleSubjects(userId: string): Promise<string[]> {
  const subs = await getUserSubscriptions(userId);
  return subs
    .filter((s) => s.status === "active")
    .filter((s) => !s.endDate || new Date(s.endDate) > new Date())
    .map((s) => s.subjectId);
}

/**
 * Returns true if the user has an active paid subscription (global unlimited).
 */
export function hasPaidSubscription(subscriptions: Subscription[]): boolean {
  return subscriptions.some(
    (s) =>
      (s.plan === "basic" || s.plan === "pro") &&
      s.status === "active" &&
      (!s.endDate || new Date(s.endDate) > new Date()),
  );
}

export function hasAiraAccess(subscriptions: Subscription[]): boolean {
  return subscriptions.some(
    (s) =>
      s.plan === "pro" &&
      s.status === "active" &&
      (!s.endDate || new Date(s.endDate) > new Date()),
  );
}

/**
 * Write a paid subscription record after a successful Stripe payment.
 */
export async function createPaidSubscription(
  userId: string,
  stripeSessionId: string,
  plan: "basic" | "pro" = "basic"
): Promise<void> {
  await databases.createDocument(
    DATABASE_ID,
    COLLECTIONS.SUBSCRIPTIONS,
    ID.unique(),
    {
      userId,
      subjectId: "global",
      plan,
      status: "active",
      startDate: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      stripeSessionId,
    }
  );
}
